import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Badge, Spinner } from '../atoms';
import {
  BUILD_STAGES, STAGE_LABELS, newMessageId,
  type Capacity, type CapacityEntry, type MessageResponse, type BuildRequest, type Quote, type ResearchState,
} from '../lib/buildMode';

/** A clean, customer-facing error message — strips the api client's "HTTP NNN:" prefix. */
function errText(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : '';
  return (raw || fallback).replace(/^HTTP \d+:\s*/, '');
}

/** A one-line inline error, used wherever a mutation or query can fail. */
function ErrLine({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>
      {errText(error, fallback)}
    </p>
  );
}

/**
 * BuildMode — /build
 *
 * The customer's own build loop, native to the AppealStack shell: describe a
 * feature to the building assistant, see what it costs as a PERCENTAGE of the
 * firm's capacity, approve it, and watch it move through the tracker.
 *
 * The capacity meter is the throttle. The customer never sees units, tokens, or
 * dollars — only a percentage — because Populus sets what 100% means per instance
 * behind the scenes.
 *
 * VOCABULARY (customer-facing vs internal, don't mix them):
 *   customer sees  "build budget" — an amount they hold, spend, and can add to
 *   the concept is  "build utilization" — how much of it a period has consumed
 *   internals keep  capacity meter / capacity ledger / cost_pct / pct_remaining,
 *                   which are accurate about the mechanism
 * "Capacity" is our word for a throttle; a firm hears a budget. Show it ONCE per
 * surface — a percentage and a bar saying the same thing twice is one thing said
 * twice, not two facts.
 *
 * ONE AUDIENCE: this whole surface is `super_admin` — the firm owner. There is no
 * approver-versus-reader split any more, so there is no per-control role check
 * either; everyone who can load this page can act on it. The server enforces it with
 * a single gate on the router, and the nav item is gated to match.
 */
export function BuildMode() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [reply, setReply] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A stable idempotency key minted per quote and sent as request_id on approve, so a
  // double-click / retry / two tabs cannot mint two builds and double-charge — the
  // server debits once per request_id. Reset after a successful build.
  const [approvalKey, setApprovalKey] = useState('');

  const capacity = useQuery<Capacity>({
    queryKey: ['build-capacity'],
    queryFn: ({ signal }) => api.get('/api/build-mode/capacity', signal),
  });

  const requests = useQuery<BuildRequest[]>({
    queryKey: ['build-requests'],
    queryFn: ({ signal }) => api.get('/api/build-mode/requests', signal),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['build-capacity'] });
    qc.invalidateQueries({ queryKey: ['build-capacity-history'] });
    qc.invalidateQueries({ queryKey: ['build-requests'] });
  };

  // Quoting is free — it never touches the meter — so the reply + quote just
  // render; nothing is charged until the customer clicks Build it.
  const askQuote = useMutation<MessageResponse, Error, string>({
    mutationFn: (body) => api.post('/api/build-mode/messages', { message_id: newMessageId(), body }),
    // Mint a fresh idempotency key for THIS quote; both approve buttons reuse it.
    onSuccess: (res) => { setReply(res.reply); setQuote(res.quote); setApprovalKey(newMessageId()); },
  });

  const approve = useMutation<BuildRequest, Error, { accelerated: boolean }>({
    mutationFn: ({ accelerated }) =>
      api.post('/api/build-mode/requests', {
        // No `category`: the server derives pricing from the text and ignores a
        // client-asserted category, so sending one is dead weight that reads as though
        // the client sets the price.
        request_text: draft, accelerated,
        request_id: approvalKey || newMessageId(), // stable key → a retry/double-click charges once
      }),
    onSuccess: (created) => {
      setDraft(''); setQuote(null); setReply(null); setApprovalKey('');
      setSelectedId(created.id);
      invalidateAll();
    },
  });

  const submitDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (draft.trim()) askQuote.mutate(draft.trim());
  };

  const list = requests.data ?? [];
  const selected = list.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{ margin: 0 }}>Custom Builds</h1>
        <p style={{ color: 'var(--color-ink-3)', marginTop: 'var(--sp-1)' }}>
          Describe a feature you want. The building assistant quotes it against your build budget,
          and you watch it ship.
        </p>
      </header>

      <CapacityMeter capacity={capacity.data} loading={capacity.isLoading} />

      {/* Conversational quote */}
      <section style={card}>
        <form onSubmit={submitDraft}>
          <label htmlFor="bm-draft" style={{ fontWeight: 600, display: 'block', marginBottom: 'var(--sp-2)' }}>
            What do you need built?
          </label>
          <textarea
            id="bm-draft"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. a report showing win rate by county"
            rows={2}
            style={textarea}
          />
          <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button type="submit" disabled={!draft.trim() || askQuote.isPending}>
              {askQuote.isPending ? 'Thinking…' : 'Get a quote'}
            </Button>
          </div>
          {askQuote.isError && <ErrLine error={askQuote.error} fallback="Couldn't get a quote — try again." />}
        </form>

        {reply && (
          <div style={{ marginTop: 'var(--sp-4)', padding: 'var(--sp-3)', background: 'var(--color-bg)', borderRadius: 'var(--r-md)' }}>
            <p style={{ margin: 0 }}>{reply}</p>
            {quote && quote.affordable && (
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                <Button onClick={() => approve.mutate({ accelerated: false })} disabled={approve.isPending}>
                  Build it
                </Button>
              </div>
            )}
            {quote && !quote.affordable && (
              <p style={{ marginTop: 'var(--sp-2)', color: 'var(--color-status-amber)', fontSize: 'var(--fs-sm)' }}>
                Not enough build budget for this build right now.
              </p>
            )}
            {approve.isError && <ErrLine error={approve.error} fallback="Could not approve the build." />}
          </div>
        )}
      </section>

      {/* The board — every build as a tracked ticket, with a status summary + filters */}
      <BuildBoard builds={list} loading={requests.isLoading} selectedId={selectedId} onSelect={setSelectedId} />

      {/* The selected build's tracker + controls open below the board */}
      {selected && (
        <BuildDetail id={selected.id} onChange={invalidateAll} />
      )}
    </>
  );
}

/** Stage groups for the board's summary + filter chips. */
const STAGE_GROUPS: { key: string; label: string; stages: string[] }[] = [
  { key: 'queued', label: 'Queued', stages: ['queued'] },
  { key: 'researching', label: 'Researching', stages: ['researching'] },
  { key: 'building', label: 'Building', stages: ['building', 'security_check'] },
  { key: 'preview', label: 'Preview', stages: ['preview'] },
  { key: 'live', label: 'Shipped', stages: ['live'] },
  { key: 'cancelled', label: 'Cancelled', stages: ['cancelled'] },
];

/** The status-dot colour per stage — the at-a-glance signal on each ticket. */
const STAGE_DOT: Record<string, string> = {
  queued: 'var(--color-ink-3)',
  researching: 'var(--color-status-amber)',
  building: 'var(--brand-primary)',
  security_check: 'var(--brand-primary)',
  preview: 'var(--brand-secondary)',
  live: 'var(--color-status-green)',
  cancelled: 'var(--color-muted)',
};

/**
 * A short, stable, human-referenceable ticket ref from the build id — so a build can be
 * named in a conversation ("how's #C123 going?") without exposing the raw id.
 */
function shortRef(id: string): string {
  return '#' + (id.replace(/[^a-z0-9]/gi, '').slice(-4).toUpperCase() || 'BUILD');
}

/**
 * The one-line "where it stands" on each ticket — plain English about status and when,
 * never a cost. The % was paid at the quote; the board doesn't re-quote it.
 */
function whenLine(b: BuildRequest): string {
  if (b.stage === 'live') return 'Shipped';
  if (b.stage === 'cancelled') return 'Cancelled';
  if (b.stage === 'preview') return 'Ready to preview';
  return b.delivery_estimate ? `Ready in ${b.delivery_estimate}` : '';
}

/**
 * BuildBoard — the customer's builds as a tracked ticket board: status filters + search
 * and a card per build. Builds are created from the chat above; this is where they're
 * tracked through to shipped. Money lives in the capacity gauge + the quote, not here.
 */
function BuildBoard({ builds, loading, selectedId, onSelect }: {
  builds: BuildRequest[]; loading: boolean; selectedId: string | null; onSelect: (id: string) => void;
}) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const count = (stages: string[]) => builds.filter((b) => stages.includes(b.stage)).length;
  const shipped = count(['live']);
  const actionable = builds.filter((b) => b.stage !== 'cancelled').length;

  if (loading) return <section style={card}><Spinner /></section>;
  if (builds.length === 0) {
    return <section style={card}><p style={{ color: 'var(--color-ink-3)', margin: 0 }}>No builds yet. Describe one above to get started.</p></section>;
  }

  const group = STAGE_GROUPS.find((g) => g.key === filter);
  const q = search.trim().toLowerCase();
  const filtered = builds.filter((b) =>
    (filter === 'all' || (group ? group.stages.includes(b.stage) : true)) &&
    (!q || b.request_text.toLowerCase().includes(q)),
  );

  return (
    <section style={card}>
      {/* Board header — a plain count of builds shipped. No second percentage here:
          capacity is the only % (the gauge above + the quote); builds are just counted. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--sp-3)' }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', margin: 0 }}>Your builds</h2>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{shipped} of {actionable} shipped</span>
      </div>

      {/* Filters + search */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <FilterChip label="All" count={builds.length} active={filter === 'all'} onClick={() => setFilter('all')} />
        {STAGE_GROUPS.map((g) => {
          const c = count(g.stages);
          return c === 0 ? null : (
            <FilterChip key={g.key} label={g.label} count={c} active={filter === g.key} onClick={() => setFilter(g.key)} />
          );
        })}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search builds…" style={searchInput} />
      </div>

      {/* Tickets */}
      {filtered.length === 0 ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', margin: 0 }}>No builds match.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {filtered.map((b) => (
            <li key={b.id}>
              <button onClick={() => onSelect(b.id)} style={{ ...ticketCard, ...(b.id === selectedId ? buildRowActive : {}) }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: STAGE_DOT[b.stage] ?? 'var(--color-ink-3)', flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', color: 'var(--color-ink-3)', marginRight: 8, fontWeight: 500 }}>{shortRef(b.id)}</span>
                    {b.request_text}
                  </span>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                    {whenLine(b)}
                  </span>
                </span>
                <StageBadge stage={b.stage} label={b.stage_label} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FilterChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
        border: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)',
        background: active ? 'var(--brand-primary)' : 'transparent',
        color: active ? 'var(--brand-on-primary)' : 'var(--color-ink-2)',
      }}
    >
      {label}<span style={{ opacity: 0.7 }}>{count}</span>
    </button>
  );
}

function CapacityMeter({ capacity, loading }: { capacity?: Capacity; loading: boolean }) {
  const [showHistory, setShowHistory] = useState(false);

  // The meter reads as USED, not remaining: it fills toward 100% and is full when the
  // firm's budget is gone. The engine reports `pct_remaining`, so the inversion happens
  // here — one line, and the API contract is untouched.
  //
  // ⚠️ `pct_remaining` is deliberately NOT defaulted to 0. A missing value means "not
  // loaded yet", and `100 - 0` would paint a FULL RED bar on every first render, which
  // reads as "you are out of budget" for the moment before the fetch lands. Undefined
  // renders as EMPTY instead, which is the honest picture of "we don't know yet".
  const remaining = capacity?.pct_remaining;
  const used = remaining == null ? 0 : Math.max(0, Math.min(100, 100 - remaining));

  // Thresholds are the SAME boundaries as before, restated against usage: red once
  // 15% or less is left, amber below 40%. Full is therefore always red.
  const colour = used >= 85
    ? 'var(--color-status-red)'
    : used >= 60
      ? 'var(--color-status-amber)'
      : 'var(--brand-primary)';

  // The statement is fetched only once the customer opens it — the meter alone is
  // enough for the common case, and this keeps the page's first load to one call.
  const historyQ = useQuery<{ entries: CapacityEntry[] }>({
    queryKey: ['build-capacity-history'],
    queryFn: ({ signal }) => api.get('/api/build-mode/capacity/history', signal),
    enabled: showHistory,
  });

  return (
    <section style={card}>
      {/* One gauge, one currency: a percentage. No build count — with per-category
          pricing there's no honest fixed number of builds, so % is all the customer sees. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
        <div style={{ fontSize: 'var(--fs-3xl)', fontWeight: 700, lineHeight: 1, color: colour }}>
          {loading ? '…' : used}<span style={{ fontSize: 'var(--fs-lg)' }}>%</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Custom Builds Usage Limit</div>
          <div
            style={{ height: 10, background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 999, overflow: 'hidden', marginTop: 6 }}
            role="progressbar"
            aria-valuenow={used}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Custom Builds usage"
          >
            <div style={{ width: `${used}%`, height: '100%', background: colour, transition: 'width .3s' }} />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)' }}
        >
          {showHistory ? 'Hide' : 'History'}
        </button>
      </div>

      {showHistory && (
        <div style={{ marginTop: 'var(--sp-3)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-3)' }}>
          {historyQ.isLoading && <Spinner />}
          {historyQ.isError && <ErrLine error={historyQ.error} fallback="Couldn't load your build budget history." />}
          {historyQ.data && historyQ.data.entries.length === 0 && (
            <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Nothing has used your build budget yet.</p>
          )}
          {historyQ.data && historyQ.data.entries.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {historyQ.data.entries.map((e, i) => (
                <li key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--sp-3)', padding: '4px 0', fontSize: 'var(--fs-sm)' }}>
                  <span style={{ color: 'var(--color-ink-2)' }}>{CAPACITY_KIND_LABEL[e.kind] ?? e.reason}</span>
                  <span style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 600, color: e.pct < 0 ? 'var(--color-status-red)' : 'var(--color-status-green)' }}>
                      {e.pct > 0 ? '+' : ''}{e.pct}%
                    </span>
                    <span style={{ color: 'var(--color-ink-3)' }}>{new Date(e.created_at).toLocaleDateString()}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/** How each movement of the firm's build budget reads to the customer. */
const CAPACITY_KIND_LABEL: Record<CapacityEntry['kind'], string> = {
  included: 'Included in your plan',
  build: 'Build approved',
  refund: 'Refund — build cancelled',
  topup: 'Added to your budget',
  adjustment: 'Adjustment',
};

function BuildDetail({ id, onChange }: { id: string; onChange: () => void }) {
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const qc = useQueryClient();
  const [fb, setFb] = useState('');

  const detail = useQuery<BuildRequest>({
    queryKey: ['build-request', id],
    queryFn: ({ signal }) => api.get(`/api/build-mode/requests/${id}`, signal),
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ['build-request', id] }); onChange(); };

  const advance = useMutation<BuildRequest, Error, string>({
    mutationFn: (toStage) => api.post(`/api/build-mode/requests/${id}/advance`, { to_stage: toStage }),
    onSuccess: refresh,
  });

  const sendFeedback = useMutation<unknown, Error, string>({
    mutationFn: (body) => api.post(`/api/build-mode/requests/${id}/feedback`, { body }),
    onSuccess: () => { setFb(''); refresh(); },
  });

  const toggleFeature = useMutation<BuildRequest, Error, boolean>({
    mutationFn: (enabled) => api.post(`/api/build-mode/requests/${id}/toggle`, { enabled }),
    onSuccess: refresh,
  });

  const cancelBuild = useMutation<BuildRequest, Error, void>({
    mutationFn: () => api.post(`/api/build-mode/requests/${id}/cancel`, {}),
    onSuccess: refresh,
  });

  if (detail.isError) {
    return (
      <section style={card}>
        <ErrLine error={detail.error} fallback="Couldn't load this build." />
        <Button variant="secondary" onClick={() => detail.refetch()} style={{ marginTop: 'var(--sp-2)' }}>Retry</Button>
      </section>
    );
  }
  if (detail.isLoading || !detail.data) return <section style={card}><Spinner /></section>;
  const r = detail.data;
  const currentIdx = BUILD_STAGES.indexOf(r.stage as typeof BUILD_STAGES[number]);
  const isCancelled = r.stage === 'cancelled';

  return (
    <section style={card}>
      <h2 style={sectionTitle}>
        <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', color: 'var(--color-ink-3)', marginRight: 8, fontWeight: 500 }}>{shortRef(r.id)}</span>
        {r.request_text}
      </h2>

      {/* A cancelled build never entered the tracker's happy path — show its state
          plainly instead of an all-grey "everything upcoming" rail. */}
      {isCancelled ? (
        <div style={{ margin: '0 0 var(--sp-4)' }}>
          <Badge variant="neutral">Cancelled</Badge>
        </div>
      ) : (
      /* Domino's-style tracker rail */
      <ol style={{ listStyle: 'none', margin: '0 0 var(--sp-4)', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}>
        {BUILD_STAGES.map((stage, i) => {
          const done = i < currentIdx, current = i === currentIdx;
          return (
            <li key={stage} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <span style={{
                padding: '2px 10px', borderRadius: 999, fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap',
                background: current ? 'var(--brand-primary)' : done ? 'var(--brand-primary-bg)' : 'var(--color-bg)',
                color: current ? 'var(--brand-on-primary)' : done ? 'var(--brand-primary)' : 'var(--color-ink-3)',
                fontWeight: current ? 600 : 400,
              }}>
                {STAGE_LABELS[stage]}
              </span>
              {i < BUILD_STAGES.length - 1 && <span style={{ color: 'var(--color-muted)' }}>›</span>}
            </li>
          );
        })}
      </ol>
      )}

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--sp-1) var(--sp-3)', margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-sm)' }}>
        <dt style={dt}>Cost</dt><dd style={dd}>{r.cost_pct}% of your build budget</dd>
        <dt style={dt}>Status</dt><dd style={dd}>{r.stage_label}</dd>
        {!r.is_terminal && r.delivery_estimate && (
          <><dt style={dt}>Expected</dt><dd style={dd}>{r.delivery_estimate}</dd></>
        )}
        <dt style={dt}>Feature</dt>
        <dd style={dd}>
          <Badge variant={r.feature_enabled ? 'success' : 'neutral'}>{r.feature_enabled ? 'On' : 'Off'}</Badge>
        </dd>
        {r.is_archived && (<><dt style={dt}>Archived</dt><dd style={dd}><Badge variant="neutral">Archived</Badge></dd></>)}
        {r.shipped_at && (<><dt style={dt}>Shipped</dt><dd style={dd}>{new Date(r.shipped_at).toLocaleDateString()}</dd></>)}
      </dl>

      {/* Feature-flag toggle — the firm's own on/off control, once the build is live */}
      {r.stage === 'live' && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Button
            variant={r.feature_enabled ? 'secondary' : 'primary'}
            onClick={() => toggleFeature.mutate(!r.feature_enabled)}
            disabled={toggleFeature.isPending}
          >
            {r.feature_enabled ? 'Turn feature off' : 'Turn feature on'}
          </Button>
          {toggleFeature.isError && <ErrLine error={toggleFeature.error} fallback="Couldn't change the feature." />}
        </div>
      )}

      {/* Research loop — nourish the request into an assignable ticket */}
      {r.stage === 'researching' && <ResearchPanel id={id} onChange={refresh} />}

      {/* Preview feedback — the "fix this" loop */}
      {r.stage === 'preview' && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 'var(--sp-1)' }}>Preview feedback</label>
          <textarea value={fb} onChange={(e) => setFb(e.target.value)} rows={2} placeholder="What should change?" style={textarea} />
          <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button variant="secondary" onClick={() => sendFeedback.mutate(fb)} disabled={!fb.trim() || sendFeedback.isPending}>
              Send feedback
            </Button>
            <Button onClick={() => advance.mutate('live')} disabled={advance.isPending}>Ship it</Button>
            <Button variant="secondary" onClick={() => advance.mutate('building')} disabled={advance.isPending}>Send back to build</Button>
          </div>
          {sendFeedback.isError && <ErrLine error={sendFeedback.error} fallback="Couldn't send feedback." />}
          {advance.isError && <ErrLine error={advance.error} fallback="Couldn't move this build." />}
        </div>
      )}

      {/* Delivery controls: the human factory advances the tracker. Excluded during
          'researching' — that gated step (researching → building) is driven by the
          ResearchPanel, which only offers it once the checklist is satisfied, so a
          generic "Advance" here would just 400 with RESEARCH_INCOMPLETE. */}
      {r.stage !== 'preview' && r.stage !== 'researching' && !r.is_terminal && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Button variant="secondary" onClick={() => advance.mutate(BUILD_STAGES[currentIdx + 1])} disabled={advance.isPending}>
            Advance to {STAGE_LABELS[BUILD_STAGES[currentIdx + 1]]}
          </Button>
          {advance.isError && <ErrLine error={advance.error} fallback="Couldn't advance this build." />}
        </div>
      )}

      {/* Cancel — available on any non-terminal build; capacity is refunded only if
          the build has not started (still queued or in research). */}
      {!r.is_terminal && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          {cancelConfirm ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                {r.stage === 'queued' || r.stage === 'researching'
                  ? 'Cancel this build? Your capacity will be refunded.'
                  : 'Cancel this build? Work has started, so capacity is not refunded.'}
              </span>
              <Button variant="danger" onClick={() => cancelBuild.mutate()} disabled={cancelBuild.isPending}>
                Confirm cancel
              </Button>
              <Button variant="ghost" onClick={() => setCancelConfirm(false)} disabled={cancelBuild.isPending}>
                Keep building
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setCancelConfirm(true)}>Cancel build</Button>
          )}
          {cancelBuild.isError && <ErrLine error={cancelBuild.error} fallback="Couldn't cancel this build." />}
        </div>
      )}

      {(r.feedback?.length ?? 0) > 0 && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <h3 style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', margin: '0 0 var(--sp-1)' }}>Feedback</h3>
          {r.feedback!.map((f) => (
            <p key={f.id} style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)' }}>• {f.body}</p>
          ))}
        </div>
      )}

      {/* Tracker history */}
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>History</summary>
        <ul style={{ listStyle: 'none', margin: 'var(--sp-2) 0 0', padding: 0 }}>
          {(r.stage_events ?? []).map((e, i) => (
            <li key={i} style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', marginBottom: 2 }}>
              {STAGE_LABELS[e.stage] ?? e.stage}{e.note ? ` — ${e.note}` : ''} · {new Date(e.created_at).toLocaleString()}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

function ResearchPanel({ id, onChange }: { id: string; onChange: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const research = useQuery<ResearchState>({
    queryKey: ['build-research', id],
    queryFn: ({ signal }) => api.get(`/api/build-mode/requests/${id}/questions`, signal),
  });

  const save = useMutation<ResearchState, Error, Record<string, string>>({
    mutationFn: (answers) => api.post(`/api/build-mode/requests/${id}/answers`, { answers }),
    onSuccess: () => { setDraft({}); qc.invalidateQueries({ queryKey: ['build-research', id] }); onChange(); },
  });

  // The gated researching → building step lives here (not the generic advance control),
  // so it's only offered once the checklist is satisfied — matching the server's gate.
  const advance = useMutation<BuildRequest, Error, void>({
    mutationFn: () => api.post(`/api/build-mode/requests/${id}/advance`, { to_stage: 'building' }),
    onSuccess: onChange,
  });

  if (research.isError) return <ErrLine error={research.error} fallback="Couldn't load the research questions." />;
  if (research.isLoading || !research.data) return <Spinner />;
  const state = research.data;

  return (
    <div style={{ marginBottom: 'var(--sp-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)' }}>
        <strong>Researching</strong>
        <Badge variant={state.complete ? 'success' : 'warning'}>
          {state.complete ? 'Ready to build' : `${state.open_questions.length} question${state.open_questions.length === 1 ? '' : 's'} open`}
        </Badge>
      </div>

      {state.open_questions.length === 0 ? (
        <div>
          <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', margin: '0 0 var(--sp-2)' }}>
            Everything the builder needs is answered. Start the build when you're ready.
          </p>
          <Button onClick={() => advance.mutate()} disabled={advance.isPending}>
            {advance.isPending ? 'Starting…' : 'Advance to Building'}
          </Button>
          {advance.isError && <ErrLine error={advance.error} fallback="Couldn't start the build." />}
        </div>
      ) : (
        <>
          <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', marginTop: 0 }}>
            A few details so we build the right thing:
          </p>
          {state.open_questions.map((q) => (
            <div key={q.key} style={{ marginBottom: 'var(--sp-2)' }}>
              <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 2 }}>{q.question}</label>
              <textarea
                value={draft[q.key] ?? ''}
                onChange={(e) => setDraft({ ...draft, [q.key]: e.target.value })}
                rows={1}
                style={textarea}
              />
            </div>
          ))}
          <Button
            variant="secondary"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending || Object.values(draft).every((v) => !v.trim())}
          >
            {save.isPending ? 'Saving…' : 'Save answers'}
          </Button>
          {save.isError && <ErrLine error={save.error} fallback="Couldn't save your answers." />}
        </>
      )}
    </div>
  );
}

function StageBadge({ stage, label }: { stage: string; label: string }) {
  const variant = stage === 'live' ? 'success' : stage === 'cancelled' ? 'neutral' : stage === 'preview' ? 'info' : 'warning';
  return <Badge variant={variant}>{label}</Badge>;
}

const card: React.CSSProperties = {
  background: 'var(--color-card)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)',
};
const sectionTitle: React.CSSProperties = { fontSize: 'var(--fs-md)', margin: '0 0 var(--sp-3)' };
const textarea: React.CSSProperties = {
  width: '100%', padding: 'var(--sp-2)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', font: 'inherit', resize: 'vertical', boxSizing: 'border-box',
};
const ticketCard: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', width: '100%', textAlign: 'left',
  padding: 'var(--sp-3)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', background: 'var(--color-card)', cursor: 'pointer',
};
const buildRowActive: React.CSSProperties = { borderColor: 'var(--brand-primary)', background: 'var(--brand-primary-bg)' };
const searchInput: React.CSSProperties = {
  marginLeft: 'auto', minWidth: 150, flex: '0 1 auto', padding: '5px 12px',
  border: '1px solid var(--color-border)', borderRadius: 999, font: 'inherit',
  fontSize: 'var(--fs-sm)', background: 'var(--color-bg)', boxSizing: 'border-box',
};
const dt: React.CSSProperties = { color: 'var(--color-ink-3)', fontWeight: 500 };
const dd: React.CSSProperties = { margin: 0 };
